import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Spinner } from '@easydent/shared-ui';
import { useChatStore } from '@/stores/chatStore';
import { getChatContacts, startDirectChat, type ChatContact } from '@/lib/api/chat';
import ChatThread from '@/components/chat/ChatThread';


/**
 * Staff messaging inside the assistant panel. The panel is narrow, so instead of the old page's
 * side-by-side panes this is a drill-in: rooms list → thread (with a back arrow). Rooms/messages
 * come from the app-wide chatStore, so unread stays correct whichever page you're on.
 */
const MessagesPanel: React.FC = () => {
  const { t } = useTranslation('common');
  const rooms = useChatStore((s) => s.rooms);
  const activeRoomId = useChatStore((s) => s.activeRoomId);
  const loadingRooms = useChatStore((s) => s.loadingRooms);
  const loadRooms = useChatStore((s) => s.loadRooms);
  const openRoom = useChatStore((s) => s.openRoom);
  const closeRoom = useChatStore((s) => s.closeRoom);

  const [picker, setPicker] = useState(false);
  const [contacts, setContacts] = useState<ChatContact[]>([]);


  const active = rooms.find((r) => r.id === activeRoomId) ?? null;



  const openPicker = async () => {
    setPicker(true);
    if (contacts.length === 0) setContacts(await getChatContacts());
  };

  const startWith = async (userId: string) => {
    const { roomId } = await startDirectChat(userId);
    setPicker(false);
    await loadRooms();
    void openRoom(roomId);
  };

  // --- Contact picker ---
  if (picker) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-line-soft px-3 py-2">
          <span className="text-sm font-medium text-body">{t('chat.startWith', 'Start a conversation')}</span>
          <Button size="sm" variant="ghost" onClick={() => setPicker(false)}>{t('common.close', 'Close')}</Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {contacts.map((c) => (
            <button key={c.userId} onClick={() => startWith(c.userId)}
              className="flex w-full items-center justify-between border-b border-line-faint px-3 py-2.5 text-left hover:bg-canvas">
              <span className="text-sm font-medium text-body">{c.name}</span>
              <span className="text-xs text-subtle-ink">{t(`roles.${c.role}`, c.role)}</span>
            </button>
          ))}
          {contacts.length === 0 && <p className="px-3 py-6 text-sm text-muted">{t('chat.noContacts', 'No other staff to message.')}</p>}
        </div>
      </div>
    );
  }

  // --- Rooms list ---
  if (!active) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-line-soft px-3 py-2">
          <span className="text-sm font-medium text-body">{t('chat.title', 'Messages')}</span>
          <Button size="sm" variant="ghost" onClick={openPicker}>{t('chat.new', 'New')}</Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingRooms && rooms.length === 0 ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : rooms.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-subtle-ink">{t('chat.noMessages', 'No messages yet')}</p>
          ) : rooms.map((r) => (
            <button key={r.id} onClick={() => void openRoom(r.id)}
              className="flex w-full items-start gap-2 border-b border-line-faint px-3 py-2.5 text-left hover:bg-canvas">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-body">
                    {r.type === 'Team' ? t('chat.team', 'Team') : r.title}
                  </span>
                  {r.type === 'Team' && <span className="rounded bg-subtle px-1 text-[10px] text-muted">{t('chat.team', 'Team')}</span>}
                  {/* A support ticket is a room like any other, but it is worth saying who the
                      other side is — the reply comes from easyDent, not a colleague. */}
                  {r.type === 'Support' && (
                    <span className="rounded bg-brand-50 px-1 text-[10px] text-brand-ink-strong">
                      {t('support.tag', 'Support')}
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-muted">{r.lastMessagePreview ?? t('chat.noMessages', 'No messages yet')}</div>
              </div>
              {r.unreadCount > 0 && <Badge>{r.unreadCount}</Badge>}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // --- Thread ---
  return (
    <ChatThread
      showSender={active.type !== 'Direct'}
      header={
        <div className="flex items-center gap-2 border-b border-line-soft px-3 py-2">
          <button onClick={closeRoom} className="text-subtle-ink hover:text-body-soft" aria-label={t('common.back', 'Back')}>←</button>
          <span className="truncate text-sm font-medium text-body">
            {active.type === 'Team' ? t('chat.team', 'Team') : active.title}
          </span>
          {active.type === 'Support' && (
            <span className="ml-auto shrink-0 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] text-brand-ink-strong">
              {t('support.tag', 'Support')}
            </span>
          )}
        </div>
      }
    />
  );
};

export default MessagesPanel;
